import { useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { useDeliveryLogs, useExportDeliveryLogs, useWeeklyMessageCounts } from '@/lib/sdk/delivery/queries';

interface FilterFormData {
  category: string;
  status: string;
  from: string;
  to: string;
  chatbot_code: string;
}

const PAGE_SIZE = 20;
export const defaultFromStr = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

export function useDeliveryLogsPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? '';

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FilterFormData>({ category: '', status: '', from: defaultFromStr, to: '', chatbot_code: '' });

  const { register, handleSubmit } = useForm<FilterFormData>({
    defaultValues: { category: '', status: '', from: defaultFromStr, to: '', chatbot_code: '' },
  });

  const { isOpen: isTestModalOpen, onOpen: openTestModal, onClose: closeTestModal } = useDisclosure();

  const filterParams = {
    ...(filters.category ? { category: filters.category } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.from ? { from: new Date(filters.from + 'T00:00:00.000Z').toISOString() } : {}),
    ...(filters.to ? { to: new Date(filters.to + 'T23:59:59.999Z').toISOString() } : {}),
    ...(filters.chatbot_code ? { chatbot_code: filters.chatbot_code } : {}),
  };

  const { data, isLoading } = useDeliveryLogs(orgId, { page, page_size: PAGE_SIZE, ...filterParams });

  const toast = useToast();
  const exportMutation = useExportDeliveryLogs(orgId);
  const handleExport = () => {
    exportMutation.mutate(filterParams, {
      onError: (err: Error) => toast.error(err.message),
    });
  };

  const { data: weeklyCountsData, isLoading: isWeeklyCountsLoading } = useWeeklyMessageCounts(orgId);
  const weeklyCounts = weeklyCountsData ?? [];
  const weeklyTotal = weeklyCounts.reduce((sum, row) => sum + row.message_count, 0);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onApplyFilters = (values: FilterFormData) => {
    setFilters(values);
    setPage(1);
  };

  return {
    orgId,
    page, setPage, pageCount, total,
    items, isLoading,
    weeklyCounts, weeklyTotal, isWeeklyCountsLoading,
    register, handleSubmit, onApplyFilters,
    isTestModalOpen, openTestModal, closeTestModal,
    handleExport, isExporting: exportMutation.isPending,
  };
}
